import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubjectLanguageVariants } from '@features/user';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditNoteIcon from '@mui/icons-material/EditNote';
import SyncIcon from '@mui/icons-material/Sync';
import { Box, Chip, CircularProgress, Skeleton, Tooltip, Typography } from '@mui/material';
import { useEventTracking } from '@services/statsig';
import type { CreateClaimRequest } from '@store/generated/claims';
import { format, parseISO } from 'date-fns';
import { useSnackbar } from 'notistack';

import {
  CONSOLIDATED_STATUS_OPTIONS,
  deriveConsolidatedStatus,
  mapStatusFilterToAPI,
  type UnbilledStatus,
} from '../../constants/statusOptions';
import { useBillingEncounters, useClaimSubmission } from '../../store';

import { BillingTable } from './BillingTable';
import type { ColumnDefinition, ConsolidatedStatus, TableAction, UnbilledSession } from './types';
import { UnsignedNotesModal } from './UnsignedNotesModal';

// Map UI field names to API sort field names
const SORT_FIELD_MAP: Record<string, 'created_at' | 'encounter_date'> = {
  date: 'encounter_date',
  // Only date is sortable for now
};

interface UnbilledSessionsTabProps {
  filters: {
    patient?: string;
    from?: string;
    to?: string;
    status?: string;
  };
  sort?: string;
  order: 'asc' | 'desc';
  page: number;
  perPage: number;
  onPageChange: (page: number) => void;
  onPerPageChange: (perPage: number) => void;
  onSort: (field: string) => void;
  onSelectedSessionsChange?: (sessions: UnbilledSession[]) => void;
}

export const UnbilledSessionsTab: React.FC<UnbilledSessionsTabProps> = ({
  filters,
  sort,
  order,
  page,
  perPage,
  onPageChange,
  onPerPageChange,
  onSort,
  onSelectedSessionsChange,
}) => {
  // Hooks
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { submitClaim, isSubmitting } = useClaimSubmission();
  const { billing: billingMetrics } = useEventTracking();
  const subjectLanguage = useSubjectLanguageVariants();
  const [creatingClaimId, setCreatingClaimId] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState<UnbilledSession | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // Map sort field to API field name
  const apiSortBy = sort && SORT_FIELD_MAP[sort] ? SORT_FIELD_MAP[sort] : undefined;

  // Map consolidated status filter to API parameters
  const apiFilters = useMemo(() => {
    if (!filters.status) {
      return {};
    }
    return mapStatusFilterToAPI(filters.status);
  }, [filters.status]);

  // Fetch encounters from API
  const { encounters, totalCount, isLoading, isFetching, error } = useBillingEncounters({
    patientId: filters.patient,
    encounterStatus: apiFilters.encounterStatus,
    billable: apiFilters.billable,
    dateFrom: filters.from,
    dateTo: filters.to,
    page,
    pageSize: perPage,
    sortBy: apiSortBy,
    sortOrder: apiSortBy ? order : undefined,
  });

  // Map API data to display format
  const mappedData: UnbilledSession[] = useMemo(
    () =>
      encounters.map(encounter => ({
        id: encounter.encounter_id,
        patientName: encounter.patient_name,
        patientId: encounter.patient_id,
        date: encounter.encounter_date,
        encounterStatus: encounter.encounter_status as UnbilledStatus,
        billable: encounter.billable,
        consolidatedStatus: deriveConsolidatedStatus(
          encounter.encounter_status as UnbilledStatus,
          encounter.billable,
        ) as ConsolidatedStatus,
        notes: encounter.notes,
      })),
    [encounters],
  );

  // Notify parent of selected sessions
  useEffect(() => {
    if (onSelectedSessionsChange) {
      const selectedSessions = mappedData.filter(session => selectedIds.includes(session.id));
      onSelectedSessionsChange(selectedSessions);
    }
  }, [selectedIds, mappedData, onSelectedSessionsChange]);

  const handleSelectionChange = useCallback((ids: string[]) => {
    setSelectedIds(ids);
  }, []);

  const columns: ColumnDefinition<UnbilledSession>[] = useMemo(
    () => [
      {
        field: 'patientName',
        header: subjectLanguage.singularCap,
        sortable: false, // API doesn't support sorting by patient name
        width: 200,
        sticky: true, // Keep patient name visible
        priority: 'high',
      },
      {
        field: 'date',
        header: 'Date of Service',
        sortable: true,
        width: 140,
        priority: 'high',
        format: (value: unknown) => {
          const date = parseISO(value as string);
          return format(date, 'MMM d, yyyy');
        },
      },
      {
        field: 'consolidatedStatus',
        header: 'Status',
        sortable: false, // API doesn't support sorting by status
        width: 150,
        priority: 'high',
        format: (value: unknown) => {
          const status = value as string;
          const option = CONSOLIDATED_STATUS_OPTIONS.find(opt => opt.value === status);

          // Determine icon based on status
          let icon;
          switch (status) {
            case 'ready_to_bill':
              icon = <CheckCircleIcon sx={{ fontSize: 16 }} />;
              break;
            case 'note_not_signed':
              icon = <EditNoteIcon sx={{ fontSize: 16 }} />;
              break;
            case 'processing':
              icon = <SyncIcon sx={{ fontSize: 16 }} />;
              break;
            case 'pending':
              icon = <AccessTimeIcon sx={{ fontSize: 16 }} />;
              break;
            default:
              icon = undefined;
          }

          const chip = (
            <Chip
              label={option?.label || status}
              icon={icon}
              size="small"
              color={option?.color || 'default'}
              variant={status === 'note_not_signed' ? 'outlined' : 'filled'}
              sx={{
                fontSize: '0.75rem',
                height: 24,
                '& .MuiChip-icon': {
                  fontSize: 16,
                },
              }}
            />
          );

          // Add tooltip for unsigned note status
          if (status === 'note_not_signed') {
            return (
              <Tooltip
                title="Please sign your note in JotPsych in order to bill for this session."
                arrow
              >
                {chip}
              </Tooltip>
            );
          }

          return chip;
        },
      },
    ],
    [subjectLanguage.singularCap],
  );

  // Handle claim creation
  const handleCreateClaim = useCallback(
    async (row: UnbilledSession) => {
      // Prevent multiple simultaneous creations
      if (creatingClaimId || isSubmitting) return;

      setCreatingClaimId(row.id);

      const claimData: CreateClaimRequest = {
        encounter_ids: [row.id], // row.id is the encounter_id
        patient_id: row.patientId,
      };

      const result = await submitClaim(claimData);

      if (result.success && result.data) {
        // Track claim creation with initial validation status
        billingMetrics.createClaim(result.data.id || '', {
          patient_id: row.patientId,
          encounter_ids: [row.id],
          source: 'unbilled_sessions',
        });

        enqueueSnackbar('Claim created successfully', { variant: 'success' });
        // Navigate to the claim edit page
        navigate(`/billing/claim/${result.data.id}/edit`);
      } else {
        const errorMessage =
          result.error && typeof result.error === 'object' && 'message' in result.error
            ? (result.error as Error).message
            : 'Failed to create claim';
        enqueueSnackbar(errorMessage, { variant: 'error' });
        setCreatingClaimId(null);
      }
    },
    [creatingClaimId, isSubmitting, submitClaim, billingMetrics, enqueueSnackbar, navigate],
  );

  const actions: TableAction<UnbilledSession>[] = useMemo(
    () => [
      {
        label: (row: UnbilledSession) => {
          const unsignedCount = row.notes?.filter(n => !n.is_signed).length || 0;
          if (unsignedCount > 1) {
            return `Sign Notes (${unsignedCount})`;
          }
          return 'Sign Note';
        },
        ariaLabel: (row: UnbilledSession) => {
          const unsignedCount = row.notes?.filter(n => !n.is_signed).length || 0;
          return `Sign ${unsignedCount > 1 ? `${unsignedCount} unsigned notes` : 'unsigned note'} for ${row.patientName}`;
        },
        icon: () => <EditNoteIcon fontSize="small" />,
        tooltip: 'Please sign your note in JotPsych in order to bill for this session.',
        onClick: (row: UnbilledSession) => {
          if (!row.notes || row.notes.length === 0) {
            enqueueSnackbar('No notes found for this encounter', { variant: 'warning' });
            return;
          }

          const unsignedNotes = row.notes.filter(note => !note.is_signed);

          if (unsignedNotes.length === 0) {
            enqueueSnackbar('All notes are already signed', { variant: 'info' });
            return;
          }

          if (unsignedNotes.length === 1) {
            // Single unsigned note - navigate directly
            navigate(`/note/${unsignedNotes[0].note_id}`);
          } else {
            // Multiple unsigned notes - show modal
            setSelectedSession(row);
            setModalOpen(true);
          }
        },
        color: 'primary',
        // Show only for note_not_signed status
        hidden: row => row.consolidatedStatus !== 'note_not_signed',
      },
      {
        label: row => (creatingClaimId === row.id ? 'Creating...' : 'Create Claim'),
        ariaLabel: row => `Create billing claim for ${row.patientName}'s session on ${row.date}`,
        icon: row =>
          creatingClaimId === row.id ? (
            <CircularProgress size={16} />
          ) : (
            <AddIcon fontSize="small" />
          ),
        onClick: row => {
          // Prevent multiple clicks while loading
          if (creatingClaimId === row.id) return;
          handleCreateClaim(row);
        },
        color: 'primary',
        variant: 'contained',
        // Show only for ready_to_bill status, but keep visible when loading
        hidden: row => row.consolidatedStatus !== 'ready_to_bill' && creatingClaimId !== row.id,
      },
    ],
    [creatingClaimId, handleCreateClaim, enqueueSnackbar, navigate],
  );

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedSession(null);
  };

  // Handle loading state
  if (isLoading && !mappedData.length) {
    return (
      <Box>
        <Box sx={{ mb: 2 }}>
          <Skeleton variant="text" width={200} height={24} />
        </Box>
        {Array.from({ length: 5 }).map((_, index) => (
          <Skeleton key={index} variant="rectangular" height={60} sx={{ mb: 1 }} />
        ))}
      </Box>
    );
  }

  // Handle error state
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Failed to load unbilled sessions. Please try again.</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {totalCount} unbilled session{totalCount !== 1 ? 's' : ''} found
          {isFetching && !isLoading && ' (updating...)'}
        </Typography>
      </Box>

      <BillingTable
        data={mappedData}
        columns={columns}
        totalItems={totalCount}
        page={page}
        perPage={perPage}
        sortField={sort}
        sortOrder={order}
        onPageChange={onPageChange}
        onPerPageChange={onPerPageChange}
        onSort={onSort}
        onSelectionChange={handleSelectionChange}
        actions={actions}
        emptyMessage="No unbilled sessions found"
      />

      {selectedSession && (
        <UnsignedNotesModal
          open={modalOpen}
          onClose={handleModalClose}
          notes={selectedSession.notes || []}
          patientName={selectedSession.patientName}
        />
      )}
    </Box>
  );
};
